import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import { useUser } from "./userContext";
import api from "../api";

const ProjectContext = createContext();

export const useProjectContext = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useUser();

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(
        "/projects",
        {
          headers: getAuthHeader(),
        }
      );
      setProjects(response.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching projects:", error);
      setError("Failed to fetch projects. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProject = async (projectId, updates) => {
    try {
      const response = await api.patch(
        `/projects/${projectId}`,
        updates,
        { headers: getAuthHeader() }
      );
      setProjects(
        projects.map((project) =>
          project._id === projectId ? { ...project, ...response.data } : project
        )
      );
      return response.data;
    } catch (error) {
      console.error("Error updating project:", error);
      throw new Error("Failed to update project. Please try again.");
    }
  };

  const fetchParticipants = async (projectId) => {
    try {
      const response = await api.get(
        `/projects/${projectId}/participants`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching participant details:", error);
      throw new Error("Failed to fetch participant details. Please try again.");
    }
  };

  const fetchProjectLogs = async (projectId) => {
    try {
      const response = await api.get(
        `/projects/${projectId}/logs`,
        { headers: getAuthHeader() }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching project logs:", error);
      throw new Error("Failed to fetch project logs. Please try again.");
    }
  };

  useEffect(() => {
    if (user?.role === "admin") {
      fetchProjects();
    } else {
      setProjects([]);
      setError(null);
      setLoading(false);
    }
  }, [user, fetchProjects]);

  const deleteProject = async (projectId) => {
    try {
      await api.delete(
        `/projects/${projectId}`,
        { headers: getAuthHeader() }
      );
      setProjects((prev) => prev.filter((p) => p._id !== projectId));
    } catch (error) {
      console.error("Error deleting project:", error);
      throw error;
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        loading,
        error,
        fetchProjects,
        updateProject,
        fetchParticipants,
        fetchProjectLogs,
        deleteProject,
      }}>
      {children}
    </ProjectContext.Provider>
  );
};
